import { TabContext, TabPanel } from "@mui/lab";
import { Box, Stack, SxProps, Tab, Tabs, Theme, useTheme } from "@mui/material";
import { TabsOwnProps } from "@mui/material/Tabs/Tabs";
import { ComponentType, ReactNode } from "react";
import { GREY, PRIMARY } from "@/config/mui/colorPalette";
import { Icon } from "../icons";

type PageTab<Id extends string> = {
  id: Id;
  name: string;
  content: ReactNode;
  icon?: ComponentType<Icon>;
  warning?: ReactNode;
  disabled?: boolean;
};

export type PageTabsProps<Id extends string> = {
  tabs: PageTab<Id>[];
  selectedTab: Id;
  onTabChange: (id: Id) => void;
  sx?: SxProps<Theme>;
} & Pick<TabsOwnProps, "variant">;

function PageTabs<Id extends string>({
  tabs = [],
  selectedTab,
  onTabChange,
  variant = "fullWidth",
  sx,
}: PageTabsProps<Id>) {
  const theme = useTheme();

  const handleTabChange = (newTabValue: Id) => {
    onTabChange(newTabValue);
  };

  const getIconColor = (tab: PageTab<Id>) => {
    if (tab.disabled) return GREY[50];
    if (selectedTab === tab.id) return PRIMARY;
    return GREY[60];
  };

  return (
    <TabContext value={selectedTab}>
      <Box sx={{ width: "100%", ...sx }}>
        <Box
          sx={{ borderBottom: 1, borderColor: theme.palette.lightGray.main }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_event, newTabValue) => handleTabChange(newTabValue)}
            variant={variant}
            scrollButtons={false}
          >
            {tabs.map((tab) => {
              const IconComponent = tab.icon;

              return (
                <Tab
                  key={tab.id}
                  label={
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                      }}
                    >
                      <Box>{tab.name}</Box>
                      {tab.warning}
                    </Stack>
                  }
                  value={tab.id}
                  disabled={tab.disabled}
                  sx={{ minHeight: "auto" }}
                  icon={
                    tab?.icon && <IconComponent color={getIconColor(tab)} />
                  }
                  iconPosition="start"
                />
              );
            })}
          </Tabs>
        </Box>

        {tabs.map((tab) => (
          <TabPanel sx={{ py: 2, px: 0 }} key={tab.id} value={tab.id}>
            {tab.content}
          </TabPanel>
        ))}
      </Box>
    </TabContext>
  );
}

export default PageTabs;
